import { stringify as qsStringify } from 'qs';

const API_KEY_PLACEHOLDER = '{api_key}';

/**
 * Drop any formatting (eg. new line symbols) when parsing and re-format it
 */
const formatPayload = (text: string) => {
  try {
    const parsed = JSON.parse(text);
    return JSON.stringify(parsed);
  } catch {
    return text.replace(/(?:\r\n|\r|\n)/g, '');
  }
};

export const getIsValidPayload = (text: string) => {
  try {
    JSON.parse(text);
  } catch (e) {
    return false;
  }
  return true;
};

const ANY_PLACEHOLDER = /\{(.*?)\}/m;
export const getIsValidEndpointPath = (text: string) => !ANY_PLACEHOLDER.test(text);

type QueryParameters = Record<string, string | number | boolean | undefined>;
const stringifyQueryParameters = (queryParameters: QueryParameters) =>
  qsStringify(queryParameters, { addQueryPrefix: true, skipNulls: true });

type PathParameters = Record<string, string | undefined>;
const parsePathParameters = (path: string, pathParameters: PathParameters) =>
  Object.entries(pathParameters).reduce(
    (final, [key, value]) => final.replaceAll(`{${key}}`, value || `{${key}}`),
    path,
  );

export const getEndpointPath = (
  server: string,
  endpoint: string,
  pathParameters: PathParameters,
  queryParameters: QueryParameters,
) => {
  const parsedQueryParameters = stringifyQueryParameters(queryParameters);
  const parsedPathParameters = parsePathParameters(endpoint, pathParameters);
  return `${server}${parsedPathParameters}${parsedQueryParameters}`;
};

interface OutputProps {
  path: string;
  endpoint: { method: string; path: string };
  apiKey: string;
  payload: string | undefined;
}

const getCurlOutput = ({ path, endpoint, apiKey, payload }: OutputProps) => {
  const parsedPayload = payload ? `--data ${JSON.stringify(formatPayload(payload))}` : '';

  return `curl --request ${endpoint.method.toUpperCase()} "${path}" --header "Authorization: RC-WSKEY ${
    apiKey ?? API_KEY_PLACEHOLDER
  }" ${parsedPayload}`;
};

const getJavaScriptOutput = ({ path, endpoint, apiKey, payload }: OutputProps) => {
  const requestContent = JSON.stringify(
    {
      method: endpoint.method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `RC-WSKEY ${apiKey ?? API_KEY_PLACEHOLDER}`,
      },
      body: payload ? formatPayload(payload) : undefined,
    },
    null,
    2,
  );

  return `fetch("${path}", ${requestContent})`;
};

const getPythonOutput = ({ path, endpoint, apiKey, payload }: OutputProps) => {
  const headers = JSON.stringify(
    {
      'Content-Type': 'application/json',
      Authorization: `RC-WSKEY ${apiKey ?? API_KEY_PLACEHOLDER}`,
    },
    null,
    2,
  );

  const jsonPayload = payload ? `, json=${formatPayload(payload)}` : '';
  return `requests.request("${endpoint.method}", "${path}", headers=${headers}${jsonPayload})`;
};

const getRequestOutput = (outputProps: OutputProps, mode: 'py' | 'js' | 'sh' = 'sh') => {
  if (mode === 'py') return getPythonOutput(outputProps);
  if (mode === 'js') return getJavaScriptOutput(outputProps);
  return getCurlOutput(outputProps);
};

export const getRequestOutputs = (outputProps: OutputProps, mode: 'py' | 'js' | 'sh' = 'sh') => {
  const output = getRequestOutput(outputProps, mode);
  if (outputProps.apiKey) return [output.replaceAll(outputProps.apiKey, '******'), output] as const;
  return [output, output] as const;
};
